import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Field } from "./Fleet";
import { Plus, X } from "lucide-react";
import { fmtDate } from "@/lib/format";

export default function Leases() {
  const { user } = useAuth();
  const [leases, setLeases] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const r = await api.get("/leases");
    setLeases(r.data);
  };
  useEffect(() => {
    load();
    if (user.role === "rentacar_admin" || user.role === "superadmin") {
      api.get("/vehicles").then((r) => setVehicles(r.data.filter((v) => v.status === "available")));
      api.get("/client-companies").then((r) => setClients(r.data));
      api.get("/drivers").then((r) => setDrivers(r.data));
    }
  }, []);

  const canManage = user.role === "rentacar_admin" || user.role === "superadmin";

  return (
    <div data-testid="leases-page">
      <PageHeader
        tag="Comercial"
        title="Contratos de Arriendo"
        description="Asigna vehículos a empresas cliente. Gestiona arriendos abiertos o con plazo definido."
        action={
          canManage && (
            <button onClick={() => setShowForm(true)} className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 flex items-center gap-2" data-testid="new-lease-btn">
              <Plus size={18} strokeWidth={2.5} /> Nuevo Contrato
            </button>
          )
        }
      />

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th>Patente</Th>
              <Th>Cliente</Th>
              <Th>Conductor</Th>
              <Th>Inicio</Th>
              <Th>Término</Th>
              <Th>Estado</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {leases.map((l) => (
              <tr key={l.id} className="border-b border-gray-200" data-testid={`lease-row-${l.id}`}>
                <td className="px-4 py-4 font-mono font-bold">{l.vehicle?.plate || "-"}</td>
                <td className="px-4 py-4 text-sm">{l.client_company?.name || "-"}</td>
                <td className="px-4 py-4 text-sm font-mono">{l.driver_id ? "Asignado" : "-"}</td>
                <td className="px-4 py-4 text-sm">{fmtDate(l.start_date)}</td>
                <td className="px-4 py-4 text-sm">{l.open_ended ? "—" : fmtDate(l.end_date)}</td>
                <td className="px-4 py-4">
                  <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 ${l.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                    {l.active ? "Activo" : "Cerrado"}
                  </span>
                </td>
                <td className="px-4 py-4 text-right">
                  {canManage && l.active && (
                    <button
                      onClick={async () => { await api.post(`/leases/${l.id}/close`); load(); }}
                      className="text-xs font-bold uppercase tracking-wider text-red-600 underline"
                      data-testid={`close-lease-${l.id}`}
                    >
                      Cerrar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {leases.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">Sin contratos.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <NewLeaseModal
          vehicles={vehicles}
          clients={clients}
          drivers={drivers}
          onClose={() => setShowForm(false)}
          onDone={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}

function NewLeaseModal({ vehicles, clients, drivers, onClose, onDone }) {
  const [form, setForm] = useState({
    vehicle_id: "",
    client_company_id: "",
    driver_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    open_ended: false,
    monthly_price: 0,
    notes: "",
    min_days_between_maintenance: 90,
    overcharge_per_day: 5000,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [newClientName, setNewClientName] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const createClient = async () => {
    setError("");
    try {
      // Create a placeholder client company by registering through a backend helper.
      // We'll just use a simple POST to companies/me path? Actually we need a server endpoint to create client companies on behalf of rentacar.
      const r = await api.post("/client-companies/quick-create", { name: newClientName });
      setForm({ ...form, client_company_id: r.data.id });
      setShowCreateClient(false);
      setNewClientName("");
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = {
        ...form,
        monthly_price: parseFloat(form.monthly_price) || 0,
        min_days_between_maintenance: parseInt(form.min_days_between_maintenance, 10) || 90,
        overcharge_per_day: parseFloat(form.overcharge_per_day) || 0,
        end_date: form.open_ended ? null : form.end_date,
        driver_id: form.driver_id || null,
      };
      await api.post("/leases", payload);
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al crear");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl">Nuevo Contrato</h2>
          <button onClick={onClose}><X size={24} strokeWidth={2.5} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5" data-testid="new-lease-form">
          <div>
            <label className="veta-tag block mb-2">Vehículo</label>
            <select required value={form.vehicle_id} onChange={update("vehicle_id")} className="w-full h-touch px-3 border border-gray-300" data-testid="lease-vehicle-select">
              <option value="">Seleccione...</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate} · {v.brand} {v.model}</option>)}
            </select>
            {vehicles.length === 0 && <p className="text-xs text-gray-500 mt-1">No hay vehículos disponibles para arrendar.</p>}
          </div>

          <div>
            <label className="veta-tag block mb-2">Empresa Cliente</label>
            {showCreateClient ? (
              <div className="flex gap-2">
                <input value={newClientName} onChange={(e) => setNewClientName(e.target.value)} placeholder="Nombre empresa cliente" className="flex-1 h-touch px-3 border border-gray-300" data-testid="new-client-name" />
                <button type="button" onClick={createClient} className="h-touch px-4 bg-black text-white text-xs font-bold uppercase">Crear</button>
                <button type="button" onClick={() => setShowCreateClient(false)} className="h-touch px-3 border border-gray-300 text-xs">×</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <select required value={form.client_company_id} onChange={update("client_company_id")} className="flex-1 h-touch px-3 border border-gray-300" data-testid="lease-client-select">
                  <option value="">Seleccione...</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" onClick={() => setShowCreateClient(true)} className="h-touch px-4 border border-gray-300 text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white" data-testid="add-client-btn">+ Nueva</button>
              </div>
            )}
          </div>

          <div>
            <label className="veta-tag block mb-2">Conductor (opcional)</label>
            <select value={form.driver_id} onChange={update("driver_id")} className="w-full h-touch px-3 border border-gray-300">
              <option value="">Sin asignar</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.rut}</option>)}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="veta-tag block mb-2">Fecha inicio</label>
              <input type="date" required value={form.start_date} onChange={update("start_date")} className="w-full h-touch px-3 border border-gray-300" />
            </div>
            <div>
              <label className="veta-tag block mb-2">Fecha término</label>
              <input type="date" disabled={form.open_ended} value={form.end_date} onChange={update("end_date")} className="w-full h-touch px-3 border border-gray-300 disabled:bg-gray-100" />
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={form.open_ended} onChange={(e) => setForm({ ...form, open_ended: e.target.checked, end_date: e.target.checked ? "" : form.end_date })} data-testid="lease-open-ended" /> Sin plazo (abierto)
              </label>
            </div>
          </div>

          <Field label="Precio mensual (CLP)" type="number" value={form.monthly_price} onChange={update("monthly_price")} testId="lease-price" />

          <div className="border-t border-gray-200 pt-5 mt-2">
            <p className="veta-tag mb-3">Control de mantención</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Mínimo días entre mantenciones" type="number" value={form.min_days_between_maintenance} onChange={update("min_days_between_maintenance")} testId="lease-min-days" />
              <Field label="Sobrecargo por día (CLP)" type="number" value={form.overcharge_per_day} onChange={update("overcharge_per_day")} testId="lease-overcharge" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Si el vehículo entra a mantención antes de cumplir el plazo mínimo, se calculará automáticamente un sobrecargo y podrás emitir una "Notificación al Cliente" en PDF.
            </p>
          </div>

          {error && <div className="status-danger px-4 py-3 text-sm">{error}</div>}
          <button type="submit" disabled={loading} className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60" data-testid="save-lease-btn">
            {loading ? "Guardando..." : "Crear Contrato"}
          </button>
        </form>
      </div>
    </div>
  );
}
